import React, { useState, useContext, useEffect } from "react";
import {
  Form,
  Button,
  Row,
  Col,
  Card,
  Container,
} from "react-bootstrap";
import {
  FaTrash,
  FaBed,
  FaBath,
  FaRulerCombined,
  FaMapMarkerAlt,
} from "react-icons/fa";
import Select from "react-select";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { InfoContext } from "../providers/InfosContextProvider";
import { useNavigate, useLocation } from "react-router-dom";
import { buildPublicAssetPath } from "../utils/publicAssetPath";
import PropertyImageCarousel from "../components/PropertyImageCarousel";
import { heroBackgroundStyle, preloadPropertyCover } from "../utils/images";

const customStyles = {
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#a02020"
      : state.isFocused
        ? "#f0f0f0"
        : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#000000",
    "&:active": {
      backgroundColor: "#a02020",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#000000",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const Properties = () => {
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedBedrooms, setSelectedBedrooms] = useState(null);
  const [selectedBathrooms, setSelectedBathrooms] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const { options, houses, texts } = useContext(InfoContext);

  useEffect(() => {
    const pageTitle =
      texts?.header?.buy || texts?.header?.home || "Imóveis";
    document.title = `${pageTitle} | Casas do Nabão`;
  }, [texts]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const transaction = params.get("transaction");
    if (options.transaction.length > 0) {
      let selected;
      if (transaction) {
        selected = options.transaction.find((opt) => opt.value === transaction);
      } else {
        selected = options.transaction.find((opt) => opt.value === "buy");
      }
      if (selected) {
        setSelectedTransaction(selected);
      }
    }
  }, [location.search, options.transaction]);

  useEffect(() => {
    if (options.sort && options.sort.length > 0 && !selectedFilter) {
      const defaultSort = options.sort.find((opt) => opt.value === "newest");
      if (defaultSort) {
        setSelectedFilter(defaultSort);
      }
    }
  }, [options.sort, selectedFilter]);

  const clearAllSelections = () => {
    setSelectedFilter(null);
    const params = new URLSearchParams(location.search);
    const transaction = params.get("transaction") || "buy";
    const defaultTransaction = options.transaction.find(
      (opt) => opt.value === transaction,
    );
    setSelectedTransaction(defaultTransaction || null);
    setSelectedType(null);
    setSelectedBedrooms(null);
    setSelectedBathrooms(null);
  };

  const handleCardClick = (id) => {
    navigate(`/property/${id}`);
  };

  const sortProperties = (properties, filter) => {
    const sorted = [...properties];
    const parsePrice = (priceStr) => {
      if (!priceStr) return 0;
      const cleanStr = priceStr.replace(/[€\s.]/g, "").replace(",", ".");
      return parseFloat(cleanStr) || 0;
    };

    switch (filter) {
      case "newest":
        return sorted.sort((a, b) => b.id - a.id);
      case "oldest":
        return sorted.sort((a, b) => a.id - b.id);
      case "priceAsc":
        return sorted.sort((a, b) => parsePrice(a.price) - parsePrice(b.price));
      case "priceDesc":
        return sorted.sort((a, b) => parsePrice(b.price) - parsePrice(a.price));
      default:
        return sorted.sort((a, b) => b.id - a.id);
    }
  };

  const filterProperties = (properties) => {
    let filteredProperties = properties;

    if (selectedTransaction) {
      filteredProperties = filteredProperties.filter(
        (property) => property.transaction === selectedTransaction.value,
      );
    }
    if (selectedType) {
      filteredProperties = filteredProperties.filter(
        (property) => property.typeProperty === selectedType.value,
      );
    }
    if (selectedBedrooms) {
      filteredProperties = filteredProperties.filter(
        (property) => property.bedrooms === Number(selectedBedrooms.value),
      );
    }
    if (selectedBathrooms) {
      filteredProperties = filteredProperties.filter(
        (property) => property.bathrooms === Number(selectedBathrooms.value),
      );
    }

    return filteredProperties;
  };

  const filteredProperties = filterProperties(houses);

  return (
    <>
      <Header />
      <div
        className="hero-background search-container"
        style={heroBackgroundStyle}
      >
        <div className="search-content">
          <Form>
            <Row className="align-items-end">
              <Col className="inputs" md={6} lg={3}>
                <Form.Group controlId="type-transaction">
                  <Select
                    options={options.transaction}
                    value={selectedTransaction}
                    onChange={setSelectedTransaction}
                    styles={customStyles}
                    placeholder={`${texts.search.transaction}`}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                  />
                </Form.Group>
              </Col>

              <Col className="inputs" md={6} lg={3}>
                <Form.Group controlId="type-property">
                  <Select
                    options={options.type}
                    value={selectedType}
                    onChange={setSelectedType}
                    styles={customStyles}
                    placeholder={`${texts.search.type}`}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                  />
                </Form.Group>
              </Col>

              <Col className="inputs" md={6} lg={3}>
                <Form.Group controlId="bedrooms">
                  <Select
                    options={options.bedrooms}
                    value={selectedBedrooms}
                    onChange={setSelectedBedrooms}
                    styles={customStyles}
                    placeholder={`${texts.search.bedrooms}`}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                  />
                </Form.Group>
              </Col>

              <Col className="inputs" md={6} lg={3}>
                <Form.Group controlId="bathrooms">
                  <Select
                    options={options.bathrooms}
                    value={selectedBathrooms}
                    onChange={setSelectedBathrooms}
                    styles={customStyles}
                    placeholder={`${texts.search.bathrooms}`}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Col md={12} lg={12} className="text-right">
              <div className="search-icon">
                <Button className="btn-clear" onClick={clearAllSelections}>
                  <FaTrash /> {`${texts.search.clear}`}
                </Button>
              </div>
            </Col>
          </Form>
        </div>
      </div>

      <Container className="p-3">
        <div className="results">
          <Row className="results-container">
            <Col xs={6} md={6} className="text-start mb-2 mb-md-0">
              <span className="bold">{filteredProperties.length}</span>
              {`${texts.results.result}`}
            </Col>
            <Col xs={0} md={4} className="d-none d-md-block"></Col>
            <Col xs="auto" className="ms-auto sort-col">
              <Form.Group
                controlId="sort"
                className="d-flex justify-content-end"
              >
                <Select
                  options={options.sort}
                  value={selectedFilter}
                  onChange={setSelectedFilter}
                  styles={customStyles}
                  placeholder={`${texts.results.sort}`}
                  isSearchable={false}
                  className="sort-select"
                  classNamePrefix="select"
                  menuPortalTarget={document.body}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
        <Row>
          {sortProperties(
            filteredProperties,
            selectedFilter ? selectedFilter.value : null,
          ).map((property) => (
            <Col key={property.id} xs={12} md={6} lg={4} className="mb-4">
              <Card
                className="property-card"
                onMouseEnter={() => preloadPropertyCover(property)}
                onTouchStart={() => preloadPropertyCover(property)}
              >
                <PropertyImageCarousel
                  property={property}
                  interval={null}
                  indicators={false}
                  onImageClick={() => handleCardClick(property.id)}
                  overlay={<div className="property-price">{property.price}</div>}
                >
                  <div className="properties-container">
                    {property.efficiency.length === 1 ? (
                      <div className="property-efficiency">
                        <img
                          src={buildPublicAssetPath(
                            `/assets/images/efficiency/${property.efficiency[0]}_RGB.png`,
                          )}
                          alt={`Eficiência Energética ${property.efficiency[0]}`}
                        />
                      </div>
                    ) : (
                      property.efficiency.map((efficiency, index) => (
                        <Col key={index} xs={6} className="property-efficiency">
                          <img
                            src={buildPublicAssetPath(
                              `/assets/images/efficiency/${efficiency}_RGB.png`,
                            )}
                            alt={`Eficiência Energética ${efficiency}`}
                          />
                        </Col>
                      ))
                    )}
                  </div>
                </PropertyImageCarousel>

                <div className="property-info">
                  <Row>
                    <Col xs={12}>
                      <span>
                        <FaMapMarkerAlt /> {property.location}
                      </span>
                    </Col>
                  </Row>
                  <Row className="justify-content-center align-items-end">
                    {property.area && (
                      <Col xs="auto">
                        <span>
                          <FaRulerCombined /> {property.area}
                        </span>
                      </Col>
                    )}
                    {property.bedrooms && (
                      <Col xs="auto">
                        <span>
                          <FaBed /> {property.bedrooms}
                        </span>
                      </Col>
                    )}
                    {property.bathrooms && (
                      <Col xs="auto">
                        <span>
                          <FaBath /> {property.bathrooms}
                        </span>
                      </Col>
                    )}
                  </Row>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
      <Footer />
    </>
  );
};

export default Properties;
